// =============================================================================
// ACT MATH PRACTICE BANK INDEX
// Comprehensive 1,000-question bank for Practice Mode
// =============================================================================
// Distribution:
// - Number & Quantity (N): 130 questions
// - Algebra (A): 175 questions
// - Functions (F): 160 questions
// - Geometry (G): 210 questions
// - Statistics & Probability (S): 150 questions
// - Integrating Essential Skills (IES): 175 questions
// TOTAL: 1,000 questions
// =============================================================================

import { algebraQuestions } from './practiceBank-algebra.js';
import { geometryQuestions } from './practiceBank-geometry.js';
import { functionsQuestions } from './practiceBank-functions.js';
import { statisticsQuestions } from './practiceBank-statistics.js';
import { numberQuantityQuestions } from './practiceBank-numberQuantity.js';
import { iesQuestions } from './practiceBank-ies.js';

// -----------------------------------------------------------------------------
// PRACTICE CATEGORIES
// -----------------------------------------------------------------------------
export const practiceCategories = [
  {
    id: 'N',
    name: 'Number & Quantity',
    icon: '🔢',
    description: 'Real/complex numbers, ratios, percentages, matrices',
    questions: numberQuantityQuestions
  },
  {
    id: 'A',
    name: 'Algebra',
    icon: '➗',
    description: 'Linear, quadratic, polynomial expressions and equations',
    questions: algebraQuestions
  },
  {
    id: 'F',
    name: 'Functions',
    icon: '📈',
    description: 'Function notation, graphs, sequences, transformations',
    questions: functionsQuestions
  },
  {
    id: 'G',
    name: 'Geometry',
    icon: '📐',
    description: 'Shapes, angles, coordinate geometry, trigonometry',
    questions: geometryQuestions
  },
  {
    id: 'S',
    name: 'Statistics & Probability',
    icon: '📊',
    description: 'Mean, median, mode, probability, data analysis',
    questions: statisticsQuestions
  },
  {
    id: 'IES',
    name: 'Integrating Essential Skills',
    icon: '🎯',
    description: 'Multi-step real-world problems combining multiple skills',
    questions: iesQuestions
  }
];

// -----------------------------------------------------------------------------
// COMBINE ALL QUESTIONS
// -----------------------------------------------------------------------------
const allPracticeQuestions = practiceCategories.flatMap(cat => 
  cat.questions.map(q => ({ ...q, category: cat.id }))
);

// -----------------------------------------------------------------------------
// HELPER FUNCTIONS
// -----------------------------------------------------------------------------

/**
 * Get random questions for practice
 * @param {number} count - Number of questions to return
 * @param {string|null} categoryId - Optional category filter
 * @returns {Array} Array of random questions
 */
export function getRandomQuestions(count, categoryId = null) {
  let pool = allPracticeQuestions;
  
  if (categoryId) {
    pool = pool.filter(q => q.category === categoryId);
  }
  
  // Fisher-Yates shuffle
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

/**
 * Get questions by difficulty level
 * @param {number} difficulty - 1, 2, or 3
 * @param {string|null} categoryId - Optional category filter
 * @returns {Array} Array of questions matching difficulty
 */
export function getQuestionsByDifficulty(difficulty, categoryId = null) {
  let pool = allPracticeQuestions;
  
  if (categoryId) {
    pool = pool.filter(q => q.category === categoryId);
  }
  
  return pool.filter(q => q.difficulty === difficulty);
}

/**
 * Get questions by topic
 * @param {string} topic - Topic name (e.g., 'linear-equations')
 * @returns {Array} Array of questions with that topic
 */
export function getQuestionsByTopic(topic) {
  return allPracticeQuestions.filter(q => 
    q.topics && q.topics.includes(topic)
  );
}

/**
 * Calculate scaled score (practice mode approximation)
 * @param {number} rawScore - Number correct
 * @param {number} totalQuestions - Total questions attempted
 * @returns {number} Scaled score (1-36)
 */
export function calculateScaledScore(rawScore, totalQuestions = 60) {
  // Approximate ACT Math scoring
  const percentage = rawScore / totalQuestions;
  
  // ACT scoring is not perfectly linear - harder to get 36
  if (percentage >= 0.98) return 36;
  if (percentage >= 0.95) return 35;
  if (percentage >= 0.90) return 33;
  if (percentage >= 0.85) return 30;
  if (percentage >= 0.80) return 27;
  if (percentage >= 0.75) return 25;
  if (percentage >= 0.70) return 23;
  if (percentage >= 0.65) return 21;
  if (percentage >= 0.60) return 19;
  if (percentage >= 0.55) return 18;
  if (percentage >= 0.50) return 17;
  if (percentage >= 0.45) return 16;
  if (percentage >= 0.40) return 15;
  if (percentage >= 0.35) return 14;
  if (percentage >= 0.30) return 13;
  if (percentage >= 0.25) return 12;
  if (percentage >= 0.20) return 11;
  
  return Math.max(1, Math.round(percentage * 36));
}

/**
 * Get all unique topics across all questions
 * @returns {Array} Array of topic strings
 */
export function getAllTopics() {
  const topics = new Set();
  allPracticeQuestions.forEach(q => {
    if (q.topics) {
      q.topics.forEach(t => topics.add(t));
    }
  });
  return Array.from(topics).sort();
}

/**
 * Get category by ID
 * @param {string} categoryId - Category ID (N, A, F, G, S, IES)
 * @returns {Object|null} Category object or null
 */
export function getCategory(categoryId) {
  return practiceCategories.find(c => c.id === categoryId) || null;
}

// -----------------------------------------------------------------------------
// STATS
// -----------------------------------------------------------------------------
export const stats = {
  totalQuestions: allPracticeQuestions.length,
  byCategory: practiceCategories.reduce((acc, cat) => {
    acc[cat.id] = cat.questions.length;
    return acc;
  }, {}),
  byDifficulty: {
    1: allPracticeQuestions.filter(q => q.difficulty === 1).length,
    2: allPracticeQuestions.filter(q => q.difficulty === 2).length,
    3: allPracticeQuestions.filter(q => q.difficulty === 3).length
  }
};

console.log(`[Math Practice] Loaded ${stats.totalQuestions} questions`);
console.log(`  N: ${stats.byCategory.N}, A: ${stats.byCategory.A}, F: ${stats.byCategory.F}`);
console.log(`  G: ${stats.byCategory.G}, S: ${stats.byCategory.S}, IES: ${stats.byCategory.IES}`);

// -----------------------------------------------------------------------------
// DEFAULT EXPORT
// -----------------------------------------------------------------------------
export default {
  practiceCategories,
  getRandomQuestions,
  getQuestionsByDifficulty,
  getQuestionsByTopic,
  calculateScaledScore,
  getAllTopics,
  getCategory,
  stats
};
